import { useCallback, useEffect, useState } from 'react';
import type { StepId, TuningSession, RestType } from '../types';
import { STEP_REGISTRY } from '../steps/registry';

const STORE_KEY = 'sparrow_tuning_sessions';

/**
 * useTuningSession — per-setup tuning state with localStorage persistence.
 *
 * One TuningSession per Setup. Switching setups loads that bow's session
 * (or creates a blank one). State persists across reloads via localStorage;
 * cross-device sync is a future hook into sparrow-sync-worker.
 *
 * Design intent: tuning sessions are forgiving — the user can leave and come
 * back days later. All progress survives.
 */
export interface UseTuningSession {
  session: TuningSession;
  setCurrentStep: (stepId: StepId) => void;
  /** Update arbitrary per-step data (each step shapes its own data). */
  updateStepData: <T>(stepId: StepId, data: T) => void;
  /** Mark a step complete + advance to the next visible step. */
  markStepComplete: (stepId: StepId) => void;
  /** Set the rest-type branch — controls which sub-step appears in the sidebar. */
  setRestType: (restType: RestType) => void;
  /** Free-text note attached to a step (surfaces in the final report). */
  setStepNote: (stepId: StepId, note: string) => void;
  /** Wipe all data for this session (with confirmation in UI). */
  resetSession: () => void;
}

export function useTuningSession(setupId: string | null): UseTuningSession {
  // Map setupId -> session. Loaded once on mount, persisted whenever it changes.
  const [allSessions, setAllSessions] = useState<Record<string, TuningSession>>(
    () => loadAllSessions(),
  );

  // Persist on every change. Map is tiny; cost is negligible.
  useEffect(() => {
    persistAllSessions(allSessions);
  }, [allSessions]);

  // Use a sentinel session if no setup is selected — keeps hooks happy
  // (always returns a stable object) while gating action functions on setupId.
  const session = setupId
    ? allSessions[setupId] ?? createBlankSession(setupId)
    : createBlankSession('_pending');

  const updateSession = useCallback(
    (patch: (prev: TuningSession) => TuningSession) => {
      if (!setupId) return;
      setAllSessions((prev) => {
        const current = prev[setupId] ?? createBlankSession(setupId);
        const updated = patch(current);
        updated.lastTouchedAt = new Date().toISOString();
        return { ...prev, [setupId]: updated };
      });
    },
    [setupId],
  );

  const setCurrentStep = useCallback(
    (stepId: StepId) => updateSession((s) => ({ ...s, currentStep: stepId })),
    [updateSession],
  );

  const updateStepData = useCallback(
    <T>(stepId: StepId, data: T) =>
      updateSession((s) => ({ ...s, stepData: { ...s.stepData, [stepId]: data } })),
    [updateSession],
  );

  const markStepComplete = useCallback(
    (stepId: StepId) =>
      updateSession((s) => {
        const completed = s.completedSteps.includes(stepId)
          ? s.completedSteps
          : [...s.completedSteps, stepId];
        // Auto-advance to next visible step
        const visibleIds = visibleStepIds(s);
        const idx = visibleIds.indexOf(stepId);
        const next = idx >= 0 && idx < visibleIds.length - 1 ? visibleIds[idx + 1]! : stepId;
        return { ...s, completedSteps: completed, currentStep: next };
      }),
    [updateSession],
  );

  const setRestType = useCallback(
    (restType: RestType) =>
      updateSession((s) => ({ ...s, branches: { ...s.branches, restType } })),
    [updateSession],
  );

  const setStepNote = useCallback(
    (stepId: StepId, note: string) =>
      updateSession((s) => ({ ...s, notes: { ...s.notes, [stepId]: note } })),
    [updateSession],
  );

  const resetSession = useCallback(() => {
    if (!setupId) return;
    setAllSessions((prev) => {
      const copy = { ...prev };
      delete copy[setupId];
      return copy;
    });
  }, [setupId]);

  return {
    session,
    setCurrentStep,
    updateStepData,
    markStepComplete,
    setRestType,
    setStepNote,
    resetSession,
  };
}

// ───────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────

function createBlankSession(setupId: string): TuningSession {
  const now = new Date().toISOString();
  return {
    setupId,
    startedAt: now,
    lastTouchedAt: now,
    currentStep: 'preflight',
    stepData: {},
    completedSteps: [],
    branches: {},
    notes: {},
  };
}

/**
 * Step IDs visible in the sidebar given current branch selection.
 * Hides the unchosen rest sub-step.
 */
function visibleStepIds(session: TuningSession): StepId[] {
  return STEP_REGISTRY
    .filter((s) => {
      if (s.id === 'rest_limb_driven') return session.branches.restType === 'limb_driven';
      if (s.id === 'rest_cable_driven') return session.branches.restType === 'cable_driven';
      return true;
    })
    .map((s) => s.id);
}

function loadAllSessions(): Record<string, TuningSession> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Record<string, TuningSession>;
  } catch {
    return {};
  }
}

function persistAllSessions(sessions: Record<string, TuningSession>): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(sessions));
  } catch {
    /* sandboxed iframe or quota — silently degrade */
  }
}
